"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCadCrossReferenceDbIdMetadataKey = exports.OrCadCrossReferenceKindMetadataKey = void 0;
exports.scaleFont = scaleFont;
exports.findPortSymbol = findPortSymbol;
exports.resolveSymbolVariant = resolveSymbolVariant;
exports.computePortPinPosition = computePortPinPosition;
exports.computeSymbolPinPosition = computeSymbolPinPosition;
exports.generateSymbolGroupDrawing = generateSymbolGroupDrawing;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformSchematicDrawing_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematicDrawing");
const TransformImageExtraction_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformImageExtraction");
const orcadLayers_1 = require("../../../orcadLayers");
const sigma_engine_shared_2 = require("@cadstrom/sigma-engine-shared");
const orcadShapeConverters_1 = require("../../../orcadShapeConverters");
const ModelText_1 = require("../../../../../ModelText");
const { translatePoint, transformDrawing } = sigma_engine_shared_1.Utils.Transform;
function scaleFont(font) {
    return Math.abs(font.Height / 2);
}
function findPortSymbol(design, port) {
    const symbols = design.Cache?.PortSymbol;
    const symbolName = port.SymbolName ?? port.Name;
    return resolveSymbolVariant(symbols, symbolName, port.LibName).symbol;
}
function canonicalSourceLibraryName(sourceLibName) {
    const normalized = (sourceLibName ?? '').trim().replace(/\\/g, '/');
    if (!normalized) {
        return '';
    }
    const prefix = normalized.startsWith('//') ? '//'
        : normalized.startsWith('/') ? '/'
            : '';
    const segments = [];
    for (const segment of normalized.split('/')) {
        if (!segment || segment === '.') {
            continue;
        }
        if (segment === '..') {
            if (segments.length > 0 && segments[segments.length - 1] !== '..') {
                segments.pop();
            }
            else if (!prefix) {
                segments.push(segment);
            }
            continue;
        }
        segments.push(segment);
    }
    return `${prefix}${segments.join('/')}`.toLowerCase();
}
function resolveSymbolVariant(symbols, symbolName, sourceLibName) {
    const candidates = symbols?.filter((symbol) => symbol.Name === symbolName) ?? [];
    const canonicalSourceLibName = canonicalSourceLibraryName(sourceLibName);
    const exact = candidates.filter((symbol) => canonicalSourceLibraryName(symbol.SourceLibName) === canonicalSourceLibName);
    if (exact.length === 1) {
        return { symbol: exact[0], ambiguous: false };
    }
    return { symbol: undefined, ambiguous: (exact.length > 0 ? exact.length : candidates.length) > 1 };
}
function computePortPinPosition(design, port) {
    const portSymbolDef = findPortSymbol(design, port);
    const pin = portSymbolDef?.PinScalar;
    if (!portSymbolDef || !pin) {
        return { position: [port.LocX, port.LocY], pinRecorded: false };
    }
    return {
        position: computeSymbolPinPosition([pin.HotptX, pin.HotptY], portSymbolDef.BoundingBox, port.LocX, port.LocY, port.Rotation ?? 0, port.Mirror ?? 0),
        pinRecorded: true,
    };
}
function computeSymbolPinPosition(pinLocal, boundingBox, locX, locY, rotation, mirror) {
    const bboxCenter = [(boundingBox.X1 + boundingBox.X2) / 2, (boundingBox.Y1 + boundingBox.Y2) / 2];
    let centered = [pinLocal[0] - bboxCenter[0], pinLocal[1] - bboxCenter[1]];
    if (mirror === 1) {
        centered = [-centered[0], centered[1]];
    }
    const rot = rotation % 4;
    if (rot === 1) {
        centered = [centered[1], -centered[0]];
    }
    else if (rot === 2) {
        centered = [-centered[0], -centered[1]];
    }
    else if (rot === 3) {
        centered = [-centered[1], centered[0]];
    }
    const pointTransform = (p) => translatePoint(p, [bboxCenter[0], bboxCenter[1]]);
    const oneWeirdTrick = (p) => translatePoint(translatePoint(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
    let moduleCenter = pointTransform([locX, locY]);
    if (rot % 2 === 1) {
        moduleCenter = oneWeirdTrick(moduleCenter);
    }
    return [moduleCenter[0] + centered[0], moduleCenter[1] + centered[1]];
}
exports.OrCadCrossReferenceKindMetadataKey = 'orcadCrossReferenceKind';
exports.OrCadCrossReferenceDbIdMetadataKey = 'orcadCrossReferenceDbId';
function generateSymbolGroupDrawing(options) {
    const { parent, symbolDef, boundingBox, locX, locY, rotation, mirror, name, displayProps, userProps, netRefObject, crossReferenceSource, } = options;
    const bboxCenter = [(boundingBox.X1 + boundingBox.X2) / 2, (boundingBox.Y1 + boundingBox.Y2) / 2];
    const pointTransform = (p) => translatePoint(p, [-bboxCenter[0], -bboxCenter[1]]);
    const symbolDrawings = orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(symbolDef);
    const bitmapResult = orcadShapeConverters_1.OrCadShapeConvert.elementBitmaps({ pointTransform })(symbolDef);
    const imageExtractions = bitmapResult.imageExtractions.map((ie) => new TransformImageExtraction_1.TransformImageExtraction(parent, ie.ref, ie.name, ie.data, ie.mimeType));
    const offsetTransform = (p) => translatePoint(p, [bboxCenter[0], bboxCenter[1]]);
    const oneWeirdTrick = (p) => translatePoint(translatePoint(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
    let symbolCenterOffset = offsetTransform([0, 0]);
    if (rotation % 2 === 1) {
        symbolCenterOffset = oneWeirdTrick(symbolCenterOffset);
    }
    const mirrored = mirror === 1 ? [rotation === 1 || rotation === 3, rotation === 2 || rotation === 0] : [false, false];
    const drawingRotation = rotation === 1 || rotation === 3 ? rotation + 2 : rotation;
    const transformedDrawings = symbolDrawings.map((d) => transformDrawing(transformDrawing(d, { mirrored, angle: drawingRotation * 90, pos: [0, 0] }), {
        mirrored: [false, false],
        angle: 0,
        pos: symbolCenterOffset,
    }));
    const namedValues = { Name: name };
    for (const prop of userProps ?? []) {
        namedValues[prop.Name] = prop.Value;
    }
    const textDrawings = [];
    for (const displayProp of displayProps ?? []) {
        const namedValue = namedValues[displayProp.Name];
        if (namedValue === undefined) {
            continue;
        }
        const fontSize = scaleFont(displayProp.Font);
        textDrawings.push({
            type: 'text',
            ...(0, ModelText_1.encodeStrippedText)({ raw: namedValue, dialects: [ModelText_1.TextDialect.Orcad] }),
            pos: translatePoint([displayProp.LocX, displayProp.LocY], [0, fontSize]),
            angle: displayProp.Rotation * 90,
            justify: [-1, -1],
            size: fontSize,
            thickness: 1,
        });
    }
    const transformedBitmapDrawings = bitmapResult.drawings.map((d) => transformDrawing(transformDrawing(d, { mirrored, angle: drawingRotation * 90, pos: [0, 0] }), {
        mirrored: [false, false],
        angle: 0,
        pos: symbolCenterOffset,
    }));
    const drawing = new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
        layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_2.SchLayerType.Symbol].id],
        drawing: {
            type: 'group',
            pos: [locX, locY],
            angle: 0,
            drawings: [...textDrawings, ...transformedDrawings, ...transformedBitmapDrawings],
            metadata: crossReferenceSource ?
                {
                    [exports.OrCadCrossReferenceKindMetadataKey]: crossReferenceSource.kind,
                    [exports.OrCadCrossReferenceDbIdMetadataKey]: crossReferenceSource.dbId,
                }
                : {},
        },
    }, netRefObject);
    return { drawing, imageExtractions };
}
//# sourceMappingURL=OrcadDsnSymbolUtils.js.map